import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PAGE_NAMES } from '../../../core/helpers/constants';
import { createEmptyGuid } from '../../../core/helpers/utility';
import { IPermission } from '../../../core/models/auth.models';
import { ICampaign } from '../../../core/models/campaigns.model';
import { ICompany } from '../../../core/models/datafeed.models';
import { IReportingPeriod } from '../../../core/models/reporting.models';
import { DataFeedService } from '../../../shared/api/datafeed.service';
import { AppService } from '../../../store/app.service';
import { ReportsApiService } from '../reports.apiservice';
import { IRollupReportDataResponse } from '../../../core/models/reports.models';
import { InstanceService } from '../../../core/instance/instance.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-reports-rollup-company-main',
  templateUrl: './reports-rollup-company-main.component.html',
  styleUrls: ['./reports-rollup-company-main.component.scss']
})
export class ReportsRollupCompanyCampaignsComponent implements OnInit, OnDestroy {
  @Input() embedded = false;
  @Input() companyIdInput = '';
  @Output() reportOnlyChange = new EventEmitter<boolean>();
  doWhat: IPermission = {} as IPermission;

  showCampaignSelection = false;

  companyData = {} as ICompany;
  platforms: Array<string> = [];
  activePlatformFilter = 'All';

  columns: Array<any> = [];
  campaignsData: Array<ICampaign> = [];
  filteredCampaignsData = new BehaviorSubject<Array<ICampaign>>([]);
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private appService: AppService,
    private fb: FormBuilder,
    private reportsService: ReportsApiService,
    private dataFeedService: DataFeedService,
    private location: Location,
    private instance: InstanceService,
    private translate: TranslateService,) {
    this.columns = [
      { title: 'NAME', field: 'name', sortable: true, searchable: true },
      // { title: this.instance.labels.clientSingular, field: 'clientName', sortable: true, searchable: true },
      { title: 'PLATFORM', field: 'platform', sortable: true, pipe: 'P_CampaignPlatformTitle', searchable: true },
      { title: this.instance.labels.dmsSingular, field: 'dmsName', sortable: true, searchable: true },
      { title: 'STAGE', field: 'stageId', sortable: true, pipe: 'P_CampaignStageTitle', searchable: true },
      { title: 'ACTIVE_SUBSCRIPTION', field: 'subscriptionActive', pipe: 'P_Boolean', searchable: true },
    ];
  }

  reportForm: FormGroup = this.fb.group({
    key: [null, [Validators.required]],
    startDate: [null, [Validators.required]],
    stopDate: [null, [Validators.required]],
    platforms: ['All', [Validators.required]],
    aggregation: ['All', [Validators.required]],
  });
  get key() { return this.reportForm.get('key') as FormControl; }
  get startDate() { return this.reportForm.get('startDate') as FormControl; }
  get stopDate() { return this.reportForm.get('stopDate') as FormControl; }
  get formPlatforms() { return this.reportForm.get('platforms') as FormControl; }
  get aggregation() { return this.reportForm.get('aggregation') as FormControl; }

  companyId = '';
  ngOnInit(): void {
    if (!this.appService.getPageTitle$().getValue()) {
      this.appService.setPageTitle$('REPORTS');
    }
    this.appService.setActivePage$(PAGE_NAMES.Reports);

    this.translate.onLangChange
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.refreshReportPeriodTitles());

    this.translate.onTranslationChange
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.refreshReportPeriodTitles());
    
    this.doWhat = this.appService.getUserRolesForPage(PAGE_NAMES.Reports);

    let id = this.companyIdInput || this.route.snapshot.paramMap.get('id');
    if (id) {
      this.companyId = id;
      this.getCompanyData(id);
      this.getCompanyAssociatedCampaigns(id);
    }
    this.getSupportedReportingPeriods();

    let orderId = this.route.snapshot.paramMap.get('orderId');
    // console.log('orderId: ', orderId);
    if (orderId) {
      this.GetRollupReportDataResponse(orderId);
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  getCompanyData(id: string) {
    if (!this.isEmptyCompanyGuid) {
      this.dataFeedService.getCompanyData(id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          // console.log(response);
          this.companyData = response.data;
        });
    }
  }

  getCompanyAssociatedCampaigns(id: string) {
    this.dataFeedService.getCompanyAssociatedCampaigns(id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        // console.log(response);
        this.campaignsData = [...response.data]
          .filter(f => f.campaignAdAccounts.length > 0)
          .map(campaign => ({ ...campaign, isSelected: false }));

        this.platforms = Array.from(new Set(this.campaignsData.map(item => item.platform)));
        this.applyPlatformFilterOnCampaigns('All');
      });
  }

  applyPlatformFilterOnCampaigns(platform: string) {
    this.activePlatformFilter = platform;

    let filtered = this.campaignsData;
    if (platform.toLowerCase() != 'all') {
      filtered = this.campaignsData
        .filter(f => f.platform.toLowerCase() == platform.toLowerCase());
    }

    this.filteredCampaignsData.next([...filtered]);
  }

  reportPeriods: Array<IReportingPeriod & { displayKey: string; title: string }> = [];
  getSupportedReportingPeriods() {
    this.reportsService.getSupportedReportingPeriods()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: Array<any>) => {
        // console.log(response); 
        this.reportPeriods = (response as Array<IReportingPeriod>).map(p => ({
          ...p,
          displayKey: this.toReportingPeriodDisplayKey(p?.key),
          title: p?.key ?? ''
        }));

        this.refreshReportPeriodTitles();

        // console.log(this.reportPeriods);
        this.onReportPeriodChange("Past Week");
      });
  }

  private refreshReportPeriodTitles(): void {
    this.reportPeriods = (this.reportPeriods ?? []).map(p => {
      const displayKey = p.displayKey;
      if (!displayKey) {
        return { ...p, title: p.key ?? '' };
      }

      const translated = this.translate.instant(displayKey);
      const title = translated && translated !== displayKey ? translated : (p.key ?? '');
      return { ...p, title };
    });
  }

  private toReportingPeriodCode(key?: string | null): string {
    const raw = (key ?? '').trim();
    if (!raw) {
      return '';
    }

    const normalized = raw
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

    const aliasMap: Record<string, string> = {
      THIS_WEEK: 'CURRENT_WEEK',
      THIS_MONTH: 'CURRENT_MONTH',
      THIS_YEAR: 'CURRENT_YEAR',
      LAST_WEEK: 'PAST_WEEK',
      LAST_30_DAYS: 'PAST_30_DAYS',
      PREVIOUS_30_DAYS: 'PAST_30_DAYS',
      LAST_MONTH: 'PREVIOUS_MONTH',
      LAST_YEAR: 'PREVIOUS_YEAR'
    };

    return aliasMap[normalized] ?? normalized;
  }

  private toReportingPeriodDisplayKey(key?: string | null): string {
    const code = this.toReportingPeriodCode(key);
    return code ? `REPORTING_PERIOD.${code}` : '';
  }

  reportformsubmitted = false;
  DateStart = '' as string | undefined | null;
  DateStop = '' as string | undefined | null;
  get selectedReportPeriod(): string | undefined | null { return this.reportForm?.controls?.key?.value; }
  get datesReadonly(): boolean { return !(this.selectedReportPeriod == 'Custom'); }
  onReportPeriodChange(selected: any) {
    const selectedPeriod = typeof selected === 'string' ? selected : (selected?.key ?? '');
    if (!selectedPeriod) {
      return;
    }

    let period = this.reportPeriods.find(f => f.key == selectedPeriod);
    this.DateStart = period?.startDate;
    this.DateStop = period?.stopDate;

    this.key.setValue(selectedPeriod);
    this.startDate.setValue(this.DateStart);
    this.stopDate.setValue(this.DateStop);

    this.active = 1;
  }

  handleAggregateChange($event: any, val: boolean) {
    var target = $event.target;
    if (target.checked) {
      this.showCampaignSelection = val;
      this.applyPlatformFilterOnCampaigns(this.activePlatformFilter);
      this.active = 1;
    }
  }

  handlePlatformsChange($event: any, val: string) {
    var target = $event.target;
    if (target.checked) {
      this.applyPlatformFilterOnCampaigns(val);
      this.active = 1;
    }
  }


  get isEmptyCompanyGuid() {
    return this.companyId == createEmptyGuid();
  }

  manuallySelectedCampaigns = [] as Array<string>;
  onSelectCheckboxChange(payload: { data: ICampaign, evt: Event }) {
    let target = payload.evt.target as HTMLInputElement
    let campaign = payload.data;

    if (target.checked) {
      this.manuallySelectedCampaigns.push(campaign.id);
    }
    else {
      while (this.manuallySelectedCampaigns.indexOf(campaign.id) >= 0) {
        this.manuallySelectedCampaigns.splice(this.manuallySelectedCampaigns.indexOf(campaign.id), 1);
      }
    }

    const campaignIndex = this.campaignsData.findIndex(item => item.id === campaign.id);
    if (campaignIndex >= 0) {
      this.campaignsData[campaignIndex].isSelected = target.checked;
    }

    this.applyPlatformFilterOnCampaigns(this.activePlatformFilter);
    // console.log(campaign.id);
    // console.log(target.checked);
    // console.log(this.manuallySelectedCampaigns);
  }

  dataLoadCompleted = false;
  showReportOnly = false;
  rollupReportDataResponse: IRollupReportDataResponse | null = null;
  rollupReportRawData: Array<any> | null = null;
  SelectCampaignsError = null as string | null;
  onFormSubmit() {
    this.SelectCampaignsError = null;
    // console.log(this.reportForm.value);

    this.reportformsubmitted = true;
    if (this.reportForm.invalid)
      return;

    if (this.reportForm.touched == true) {
      this.DateStart = this.startDate.value;
      this.DateStop = this.stopDate.value;
    }

    // console.log('this.aggregation.value: ', this.aggregation.value);
    if (this.aggregation.value == 'SelectCampaigns' && this.manuallySelectedCampaigns.length == 0) {
      this.SelectCampaignsError = 'PLEASE_SELECT_CAMPAIGNS';
      return;
    }

    var campaigns = [] as Array<string>;
    if (this.aggregation.value == 'SelectCampaigns') {
      campaigns = this.manuallySelectedCampaigns;
    }
    else {
      campaigns = [createEmptyGuid()];
    }
    // console.log('campaigns: ', campaigns);

    let params = {
      companyId: this.companyId,
      reportFormat: 'html',
      campaigns: campaigns,
      aggregation: this.aggregation.value,
      platforms: this.formPlatforms.value,
      dateStart: this.startDate.value,
      dateStop: this.stopDate.value,
    };


    var paramsUrl = "" + this.router.createUrlTree([''], { queryParams: params });
    paramsUrl = paramsUrl.replace('/', '');
    // console.log(paramsUrl);

    this.rollupReportDataResponse = null;
    this.rollupReportRawData = null;
    this.reportsService.startRollupReportProcess(paramsUrl)
      .subscribe(res => {
        this.rollupReportDataResponse = res.data;
        if (this.rollupReportDataResponse?.orderId) {
          this.dataLoadCompleted = false;
          this.active = 2;
        }
      });
  }

  GetRollupReportDataResponse(orderId: string) {
    this.reportsService.getRollupReportData(orderId)
      .subscribe(res => {
        if (res?.good && res.data) {
          this.rollupReportDataResponse = res.data;
          this.DataLoadCompleted(true);
        }
        else{
          if (!this.embedded) {
            this.location.go(`/pages/reports/rollup-campaigns/${this.companyId}`);
          }
        }
      });
  }

  active: number = 1;
  onTabChange(event: number) {
    // console.log(event); 
    // Do Nothing on tab change 
  }

  DataLoadCompleted(reports: Array<any> | boolean) {
    this.rollupReportRawData = Array.isArray(reports) ? reports : null;
    this.dataLoadCompleted = true;
    this.active = 3;
    this.showReportOnly = true;
    this.reportOnlyChange.emit(this.showReportOnly);

    window.scrollTo(0, 0);

    if (!this.embedded) {
      this.location.go(`/pages/reports/rollup-report/viewer/${this.companyId}/${this.rollupReportDataResponse?.orderId}`);
    }

    // const url = this.router.serializeUrl(
    //   this.router.createUrlTree(['/reports/view-rollup-report', this.rollupReportDataResponse?.orderId ?? createEmptyGuid() ])
    // );
    // window.open(url, '_blank');
  }

  showReportSettings() {
    this.showReportOnly = false;
    this.active = 1;
    this.reportOnlyChange.emit(false);
  }

}
